import * as CORE from 'app/model/CORE';
import {
    Airblock,
    Airblock3d,
    Airspace,
    AirspaceRef,
    CountType,
    DatasetDto,
    DatasetType,
    FlightDto,
    FlightLevelBand,
    FlightModelType,
    HotspotDto,
    LAT_LON_ALT_POINT_CLAZZ_VALUE,
    LatLonAltPoint,
    LatLongBounds,
    LatLonPoint,
    LoadClassification,
    MapTrafficVolume,
    RefLocationType,
    TimePeriod,
} from 'app/model/CORE';
import { FlightAndModelType } from 'app/components/map/model/MapModel';
import Utils, { Comparator } from 'app/common/util/Utils';
import * as turf from '@turf/turf';
import MathUtils from 'app/common/util/MathUtils';
import TurfUtils from 'app/components/map/util/TurfUtils';
import { Feature, MultiPolygon, Polygon } from '@turf/helpers';
import FlightLevelUtils from 'app/common/util/FlightLevelUtils';
import {
    AccUnitDto,
    ConvectionFraction,
    ConvectionSource,
    ExtendedMonitoredTv,
    Likeliness,
    MonitoredMeasure,
    NetworkMeteoForecast,
    RegulationOutlook,
} from 'app/model/SOL49';
import MonitoredLoadUtils from 'app/common/util/MonitoredLoadUtils';
import { MapObjectType, SolutionMeasureImpact, SolutionTvImpact } from 'app/components/dashboard/network/map/Map';
import memoizeOne from 'memoize-one';
import { MapConvection } from 'app/components/map/CoreMap';
import TimePeriodUtils from 'app/common/util/TimePeriodUtils';
import OLMapStyles from 'app/components/map/ol/OLMapStyles';
import * as style from 'ol/style';
import TimeUtils from 'app/common/util/TimeUtils';
import AppContext from 'app/common/context/AppContext';
import { ColorLike } from 'ol/colorlike';
import { Coordinate } from 'ol/coordinate';

export enum LayerType {
    TFV,
    MEASURE,
    WEATHER,
    CUSTOM,
}

export enum MapElement {
    MONITORED_TV,
    LABEL,
    CONVECTION,
    MEASURE,
}

export interface MonitorableMapTv {
    tv: ExtendedMonitoredTv;
    mapTv: MapTrafficVolume;
    index: number;
    selected?: boolean;
}

export interface TvImpactMap {
    impact: SolutionTvImpact;
    mapTv: MapTrafficVolume;
}

export interface MonitorableMapMeasure {
    measure: MonitoredMeasure;
    mapTv: any;
    index: number;
    selected: boolean;
}

export interface HotspotMap {
    hotspot: HotspotDto;
    mapTv: any;
    index: number;
    selected: boolean;
}

export interface RegulationOutlookMap {
    outlook: RegulationOutlook;
    index: number;
    selected: boolean;
}

export interface MapMeasureImpact {
    measure: SolutionMeasureImpact;
    mapTv: MapTrafficVolume;
    selected: boolean;
}

export interface AccMap {
    acc: AccUnitDto;
    selected: boolean;
}

export interface MapClickElement {
    type: MapObjectType;
    subject: string;
    properties?: { [key: string]: any };
    layerId: number;
}

export enum AlertType {
    NEUTRAL = 'NEUTRAL',
    ORANGE = 'ORANGE',
    RED = 'RED',
}

export default class MapUtils {
    static readonly METRE_PER_FOOT = 0.3048;
    static readonly FEET_PER_METRE = 3.2084;
    static readonly FEET_PER_FLIGHT_LEVEL = 100;
    static readonly METRES_PER_FLIGHT_LEVEL = MapUtils.FEET_PER_FLIGHT_LEVEL * MapUtils.METRE_PER_FOOT;

    static readonly WORKSPACE_MAP_LAYER = 8;

    static displayCesiumMap() {
        return Utils.toUpperCase(import.meta.env.VITE_CESIUM as string) === 'TRUE';
    }

    static getSubjectKey(flightAndModelType: FlightAndModelType) {
        return this.getSubjectKey_(flightAndModelType.flight, flightAndModelType.modelType);
    }

    //TODO: I couldn't overload the method...
    static getSubjectKey_(flight: FlightDto, modelTpype: FlightModelType): string {
        if (flight) {
            const proposal = flight.proposalContext ? flight.proposalContext : '';
            const fkey = flight.flightId.id
                ? flight.flightId.id
                : flight.flightId.flightKeys.callsign +
                  flight.flightId.flightKeys.adep +
                  flight.flightId.flightKeys.ades;
            const dataset = this.getDatasetKey(flight.dataset);
            return fkey + '-' + modelTpype + '-' + proposal + '-' + dataset;
        } else {
            return null;
        }
    }

    //TODO: maybe it shouldn't be here
    static getDatasetKey(dataset: DatasetDto): string {
        if (dataset) {
            switch (dataset.type) {
                case DatasetType.FORECAST:
                    return 'F';
                case DatasetType.OPERATIONAL:
                    return 'O';
                case DatasetType.SIMULATION:
                    return 'S' + dataset.simulIdentifier;
            }
        }
        return '??';
    }

    /*static hasName(flightPoint : FlightPointDto) : boolean {
        return flightPoint.aerodrome != null || (flightPoint.point != null && flightPoint.point.pointId != null);
    }

    static getName(flightPoint : FlightPointDto) : string {
        if(flightPoint.aerodrome) {
            return flightPoint.aerodrome;
        } else if (flightPoint.point == null) {
            return null;
        } else {
            return flightPoint.point.pointId;
        }
    }*/

    static toDMSFormat(coordinate: number[]) {
        const latA = Math.abs(coordinate[1]);
        const lonA = Math.abs(coordinate[0]);
        const ilatA = latA | 0;
        const ilonA = lonA | 0;
        const sb: (string | number)[] = [];
        sb.push(ilatA);
        sb.push('\u00b0');
        sb.push(((latA - ilatA) * 60) | 0);
        sb.push(coordinate[1] >= 0 ? 'N' : 'S');
        sb.push(' / ');
        sb.push(ilonA);
        sb.push('\u00b0');
        sb.push(((lonA - ilonA) * 60) | 0);
        sb.push(coordinate[0] >= 0 ? 'E' : 'W');
        return sb.join('');
    }

    static getDefaultSelectedModelType = (flight: FlightDto): FlightModelType => {
        if (!flight) return null;
        let returnValue: FlightModelType = null;
        if (Utils.isNotEmpty(flight.ctfmPoints)) {
            returnValue = FlightModelType.CTFM;
        } else if (Utils.isNotEmpty(flight.rtfmPoints)) {
            returnValue = FlightModelType.RTFM;
        } else if (Utils.isNotEmpty(flight.ftfmPoints)) {
            returnValue = FlightModelType.FTFM;
        }
        return returnValue;
    };

    static isFlightModelTypeAvailable = (flight: FlightDto, modelType: FlightModelType): boolean => {
        if (!flight) return false;
        switch (modelType) {
            case FlightModelType.CTFM:
                return Utils.isNotEmpty(flight.ctfmPoints);
            case FlightModelType.RTFM:
                return Utils.isNotEmpty(flight.rtfmPoints);
            case FlightModelType.FTFM:
                return Utils.isNotEmpty(flight.ftfmPoints);
        }
        return false;
    };

    // Commented after copy from react project
    /*
    static retrieveCurrentExtent(props: AbstractMapQueryPanelProps<any, any>): LatLongBounds {
        return props.map.getExtent();
    }*/

    public static bearingToAngle(bearing: number): number {
        //=MOD(450-Bearing;360)
        return (450 - bearing) % 360;
    }

    public static angleToBearing(angle: number): number {
        return (450 - angle) % 360;
    }

    public static getLineAngleInBearing(p1: LatLonPoint, p2: LatLonPoint): number {
        //turfjs.bearing returns a value between -180 and 180
        const bearing = turf.bearing(TurfUtils.toPoint(p1), TurfUtils.toPoint(p2));
        if (bearing >= 0) {
            return bearing;
        } else {
            return bearing + 360;
        }
    }

    public static readonly NmToKilometers = 1.852;

    public static calculateCirclePoints(
        center: LatLonPoint,
        radiusInNauticalMiles: number,
        numberOfPoints: number,
    ): Array<LatLonPoint> {
        const polygonFeature = turf.circle(TurfUtils.toPoint(center), radiusInNauticalMiles * MapUtils.NmToKilometers, {
            steps: numberOfPoints,
            units: 'kilometers',
        });
        return polygonFeature.geometry.coordinates[0].map(p => TurfUtils.toLatLonPoint(p));
    }

    public static calculateCirclePointsFromAndTo(
        center: LatLonPoint,
        radiusInNauticalMiles: number,
        numberOfPoints: number,
        startBearing: number,
        endBearing: number,
    ): Array<LatLonPoint> {
        const feature = turf.lineArc(
            TurfUtils.toPoint(center),
            radiusInNauticalMiles * MapUtils.NmToKilometers,
            startBearing,
            endBearing,
            { steps: numberOfPoints },
        );
        return feature.geometry.coordinates.map(p => TurfUtils.toLatLonPoint(p));
    }

    /**
     * Is using turf.js to calculate the closest point on the reference to the given remotepoint.
     * Based on the shape it's calculated the shape outlines and then using turf calculate the closest point between
     * those lines and the given point.
     * @param shape
     * @param remotePoint
     */
    public static getClosestPointBetweenShapeAndPoint(
        shape: Array<LatLonPoint>,
        remotePoint: LatLonPoint,
    ): LatLonPoint {
        const numberOfEdges = shape.length > 1 ? shape.length - 1 : 0;
        let distance: number = Number.MAX_SAFE_INTEGER;
        let closestPoint: LatLonPoint = null;
        for (let i = 0; i < numberOfEdges; i++) {
            const i2 = (i + 1) % shape.length;
            const closestPointOnLine = MapUtils.getClosestPointBetweenLineAndPoint(shape[i], shape[i2], remotePoint);
            const distanceBetweenClosestPoint = MapUtils.distanceBetweenPointsInNauticalMiles(
                closestPointOnLine,
                remotePoint,
            );
            if (distanceBetweenClosestPoint < distance) {
                distance = distanceBetweenClosestPoint;
                closestPoint = closestPointOnLine;
            }
        }

        return closestPoint;
    }

    public static distanceBetweenPointsInNauticalMiles(p1: LatLonPoint, p2: LatLonPoint): number {
        return turf.distance(TurfUtils.toPoint(p1), TurfUtils.toPoint(p2), { units: 'nauticalmiles' });
    }

    public static closestPointsBetweenShapes(
        shape1: Array<LatLonPoint>,
        shape2: Array<LatLonPoint>,
        draw?: (line1Start: LatLonPoint, line1End: LatLonPoint, line2Start: LatLonPoint, line2End: LatLonPoint) => void,
    ): Array<LatLonPoint> {
        //clean shapes
        if (Utils.isEquals(shape1[0], shape1[shape1.length - 1])) {
            shape1 = shape1.slice(0, shape1.length - 1);
        }
        if (Utils.isEquals(shape2[0], shape2[shape2.length - 1])) {
            shape2 = shape2.slice(0, shape2.length - 1);
        }

        let pointInShape1ClosestToShape2A: LatLonPoint = null;
        let pointInShape1ClosestToShape2Idx: number = -1;
        let minDistance: number = Number.MAX_SAFE_INTEGER;
        for (let i = 0; i < shape1.length; i++) {
            const p1: LatLonPoint = shape1[i];
            const p: LatLonPoint = MapUtils.getClosestPointBetweenShapeAndPoint(shape2, p1);
            const d: number = MapUtils.distanceBetweenPointsInNauticalMiles(p1, p);
            //logger.info("A distance: "+d);
            if (d < minDistance) {
                minDistance = d;
                pointInShape1ClosestToShape2A = p1;
                pointInShape1ClosestToShape2Idx = i;
            }
        }

        //get the i adjacent points: i-1 and i +1
        const p1: LatLonPoint = shape1[MathUtils.floorMod(pointInShape1ClosestToShape2Idx - 1, shape1.length)];

        const p2: LatLonPoint = shape1[MathUtils.floorMod(pointInShape1ClosestToShape2Idx + 1, shape1.length)];

        //calculate which adjacent point is closest to shape 2
        const d1: number = MapUtils.distanceBetweenPointsInNauticalMiles(
            p1,
            MapUtils.getClosestPointBetweenShapeAndPoint(shape2, p1),
        );
        const d2: number = MapUtils.distanceBetweenPointsInNauticalMiles(
            p2,
            MapUtils.getClosestPointBetweenShapeAndPoint(shape2, p2),
        );

        const pointInShape1ClosestToShape2B: LatLonPoint = d1 < d2 ? p1 : p2;

        if (Utils.isEquals(pointInShape1ClosestToShape2A, pointInShape1ClosestToShape2B)) {
            //shouldn't reach this point
            return null;
        }

        //get the closest lines to A and B (A - shape 1 closest point to shape 2, and B it's A adjacent point closest to shape 2)
        const segmentIdxA: number = MapUtils.getClosestLineSegmentStartPointIndex(
            shape2,
            pointInShape1ClosestToShape2A,
        );
        const segmentIdxB: number = MapUtils.getClosestLineSegmentStartPointIndex(
            shape2,
            pointInShape1ClosestToShape2B,
        );

        //logger.info("segmentIdxA: "+segmentIdxA);
        //logger.info("segmentIdxB: "+segmentIdxB);
        let linesDistance: number = Number.MAX_SAFE_INTEGER;
        let shape2ClosestSegmentStart: LatLonPoint = null,
            shape2ClosestSegmentEnd = null;
        let j: number = segmentIdxA;
        do {
            const start: LatLonPoint = shape2[j];
            j = MathUtils.floorMod(j + 1, shape2.length);
            const end: LatLonPoint = shape2[j];
            const d: number = MapUtils.distanceBetweenLinesInNauticalMiles(
                pointInShape1ClosestToShape2A,
                pointInShape1ClosestToShape2B,
                start,
                end,
            );
            if (d < linesDistance) {
                linesDistance = d;
                shape2ClosestSegmentStart = start;
                shape2ClosestSegmentEnd = end;
            }
        } while (j !== segmentIdxB);

        if (shape2ClosestSegmentStart == null || shape2ClosestSegmentEnd == null) return null;

        if (Utils.isEquals(shape2ClosestSegmentStart, shape2ClosestSegmentEnd)) {
            return null;
        }

        if (draw) {
            draw(
                pointInShape1ClosestToShape2A,
                pointInShape1ClosestToShape2B,
                shape2ClosestSegmentStart,
                shape2ClosestSegmentEnd,
            );
        }
        return MapUtils.getClosestPointsBetweenLines(
            pointInShape1ClosestToShape2A,
            pointInShape1ClosestToShape2B,
            shape2ClosestSegmentStart,
            shape2ClosestSegmentEnd,
        );
    }

    public static getClosestPointsBetweenLines(
        line1Start: LatLonPoint,
        line1End: LatLonPoint,
        line2Start: LatLonPoint,
        line2End: LatLonPoint,
    ): Array<LatLonPoint> {
        const line1 = TurfUtils.toLineString(line1Start, line1End);
        const line2 = TurfUtils.toLineString(line2Start, line2End);

        const pointFeatureCollection = turf.lineIntersect(line1, line2);
        if (pointFeatureCollection.features?.length > 0) {
            return [TurfUtils.toLatLonPoint(pointFeatureCollection.features[0].geometry.coordinates)];
        }

        let distance = Number.MAX_SAFE_INTEGER;
        let coordinateOnLine1: turf.Feature<turf.Point> = null;
        let coordinateOnLine2: turf.Feature<turf.Point> = null;

        //compute min distance between line 1 vertices to line 2

        //from line 1 start vertices to line 2
        const line1StartPoint = TurfUtils.toPoint(line1Start);
        let nearestPointOnLine = turf.nearestPointOnLine(line2, line1StartPoint);
        let nearestPointOnLineDistance = turf.distance(line1StartPoint, nearestPointOnLine);
        if (nearestPointOnLineDistance < distance) {
            coordinateOnLine1 = line1StartPoint;
            coordinateOnLine2 = nearestPointOnLine;
            distance = nearestPointOnLineDistance;
        }

        //from line 1 end vertices to line 2
        const line1EndPoint = TurfUtils.toPoint(line1End);
        nearestPointOnLine = turf.nearestPointOnLine(line2, line1EndPoint);
        nearestPointOnLineDistance = turf.distance(line1EndPoint, nearestPointOnLine);
        if (nearestPointOnLineDistance < distance) {
            coordinateOnLine1 = line1EndPoint;
            coordinateOnLine2 = nearestPointOnLine;
            distance = nearestPointOnLineDistance;
        }

        //compute min distance between line 2 vertices to line 1

        //from line 2 start vertices to line 1
        const line2StartPoint = TurfUtils.toPoint(line2Start);
        nearestPointOnLine = turf.nearestPointOnLine(line1, line2StartPoint);
        nearestPointOnLineDistance = turf.distance(line2StartPoint, nearestPointOnLine);
        if (nearestPointOnLineDistance < distance) {
            coordinateOnLine2 = line2StartPoint;
            coordinateOnLine1 = nearestPointOnLine;
            distance = nearestPointOnLineDistance;
        }

        //from line 1 end vertices to line 2
        const line2EndPoint = TurfUtils.toPoint(line2End);
        nearestPointOnLine = turf.nearestPointOnLine(line1, line2EndPoint);
        nearestPointOnLineDistance = turf.distance(line2EndPoint, nearestPointOnLine);
        if (nearestPointOnLineDistance < distance) {
            coordinateOnLine2 = line2EndPoint;
            coordinateOnLine1 = nearestPointOnLine;
            distance = nearestPointOnLineDistance;
        }

        return [
            TurfUtils.toLatLonPoint(coordinateOnLine1.geometry.coordinates),
            TurfUtils.toLatLonPoint(coordinateOnLine2.geometry.coordinates),
        ];
    }

    public static getClosestPointBetweenLineAndPoint(
        line1Start: LatLonPoint,
        line1End: LatLonPoint,
        point: LatLonPoint,
    ): LatLonPoint {
        const line = TurfUtils.toLineString(line1Start, line1End);
        const turfPoint = TurfUtils.toPoint(point);
        const nearestPointOnLine = turf.nearestPointOnLine(line, turfPoint);
        return TurfUtils.toLatLonPoint(nearestPointOnLine.geometry.coordinates);
    }

    public static distanceBetweenLineAndPointInNauticalMiles(
        lineStart: LatLonPoint,
        lineEnd: LatLonPoint,
        point: LatLonPoint,
    ) {
        const pointToLineDistanceInKilometers = turf.pointToLineDistance(
            TurfUtils.toPoint(point),
            TurfUtils.toLineString(lineStart, lineEnd),
        );
        return pointToLineDistanceInKilometers / MapUtils.NmToKilometers;
    }

    public static distanceBetweenLinesInNauticalMiles(
        line1Start: LatLonPoint,
        line1End: LatLonPoint,
        line2Start: LatLonPoint,
        line2End: LatLonPoint,
    ): number {
        const closestPoints = MapUtils.getClosestPointsBetweenLines(line1Start, line1End, line2Start, line2End);
        if (!closestPoints) {
            console.warn('error calculating distance between lines');
            return 0;
        }
        //they intersect
        if (closestPoints.length === 1) {
            return 0;
        }
        return MapUtils.distanceBetweenPointsInNauticalMiles(closestPoints[0], closestPoints[1]);
    }

    public static getClosestLineSegmentStartPointIndex(shape: Array<LatLonPoint>, remotePoint: LatLonPoint): number {
        const turfPoint = TurfUtils.toPoint(remotePoint);
        const numberOfEdges = shape.length > 1 ? shape.length - 1 : 0;
        let distance: number = Number.MAX_SAFE_INTEGER;
        let closestPointIdx: number = null;

        for (let i = 0; i < numberOfEdges; i++) {
            const i2 = (i + 1) % shape.length;
            const line = TurfUtils.toLineString(shape[i], shape[i2]);
            const nearestPointOnLine = turf.nearestPointOnLine(line, turfPoint, { units: 'nauticalmiles' });
            const distanceFromRemotePointInNauticalMiles: number = turf.distance(turfPoint, nearestPointOnLine, {
                units: 'nauticalmiles',
            });
            if (distanceFromRemotePointInNauticalMiles < distance) {
                distance = distanceFromRemotePointInNauticalMiles;
                closestPointIdx = i;
            }
        }
        return closestPointIdx;
    }

    public static distanceBetweenShapeAndPointInNauticalMiles(shape: Array<LatLonPoint>, p: LatLonPoint) {
        const closestPoint = MapUtils.getClosestPointBetweenShapeAndPoint(shape, p);
        return MapUtils.distanceBetweenPointsInNauticalMiles(closestPoint, p);
    }

    public static newLatLonAltPoint(x: number, y: number, z: number): LatLonAltPoint {
        return {
            clazz: LAT_LON_ALT_POINT_CLAZZ_VALUE,
            latitude: y,
            longitude: x,
            level: { value: z }, //TODO should I add the unit?
        };
    }

    public static boundsContainsPoint(bounds: LatLongBounds, point: LatLonPoint): boolean {
        return (
            point.latitude >= bounds.minLat &&
            point.latitude <= bounds.maxLat &&
            point.longitude >= bounds.minLon &&
            point.longitude <= bounds.maxLon
        );
    }

    public static getShapeBounds(shape: Array<LatLonPoint>): LatLongBounds {
        let minLat, maxLat, minLon, maxLon;

        for (const latLonPoint of shape) {
            if (Utils.isNotExists(minLat) || minLat > latLonPoint.latitude) {
                minLat = latLonPoint.latitude;
            }
            if (Utils.isNotExists(maxLat) || maxLat < latLonPoint.latitude) {
                maxLat = latLonPoint.latitude;
            }

            if (Utils.isNotExists(minLon) || minLon > latLonPoint.longitude) {
                minLon = latLonPoint.longitude;
            }
            if (Utils.isNotExists(maxLon) || maxLon < latLonPoint.longitude) {
                maxLon = latLonPoint.longitude;
            }
        }

        return {
            minLat: minLat,
            maxLat: maxLat,
            minLon: minLon,
            maxLon: maxLon,
        };
    }

    /**
     * This method takes the airspace definition (airblocks) and join them as one big airblock.
     * It is very unlikely that is not able to join the airblocks but could happen, when for example all the airblocks are not contiguous
     * @param airspace
     */
    public static getAirspaceOutline(airspace: Airspace): Airspace {
        // console.time(airspace.reference.id + " getAirspaceOutline");
        const polygons: Feature<Polygon>[] = [];
        for (const airspace1 of airspace.composedAirspace) {
            polygons.push(...this.buildPolygons(airspace1));
        }
        for (const ab3 of airspace.airblocks) {
            if (ab3.airblock && ab3.airblock.points) {
                polygons.push(this.buildPolygon(ab3));
            }
        }
        // Union polygons together
        let unionPolygon: Feature<Polygon | MultiPolygon> = (polygons.length > 0 && polygons[0]) || undefined;
        for (let i = 1; i < polygons.length; i++) {
            unionPolygon = turf.union(unionPolygon, polygons[i]); //union expects a fixed number of parameters so spread arg can't be passed
        }
        if (unionPolygon.geometry.type === 'Polygon') {
            const unionLatLongCoordinates: Array<LatLonPoint> = unionPolygon.geometry.coordinates[0].map(position =>
                TurfUtils.toLatLonPoint(position),
            );
            const airblock: Airblock3d = {
                airblock: {
                    points: unionLatLongCoordinates,
                    id: 'union',
                },
            };
            const airspaceOutline: Airspace = {
                airblocks: [airblock],
                reference: airspace.reference,
                skipInMs: airspace.skipInMs,
                skipOutMs: airspace.skipOutMs,
                composedAirspace: [],
            };
            // console.timeEnd(airspace.reference.id + " getAirspaceOutline");
            return airspaceOutline;
        } else {
            // console.timeEnd(airspace.reference.id + " getAirspaceOutline");
            //if the union returns a MultiPolygon is because the polygons are not contiguous (should not happen in a airspace definition)
            return undefined;
        }
    }

    public static getAirspacesOutline(airspaces: Array<Airspace>): Airblock3d[] {
        const individuallyOutlineAsp: Array<Airspace> = [];
        const aspNotContiguous: Array<Airspace> = [];
        for (const asp of airspaces) {
            const outline = this.getAirspaceOutline(asp);
            if (outline) {
                individuallyOutlineAsp.push(outline);
            } else {
                //acc that is not able to generate a single airspace outline, show them as it is
                aspNotContiguous.push(asp);
                console.log('Not able to compute airspace outline', asp);
            }
        }
        // Union polygons together
        let unionPolygon: Feature<Polygon | MultiPolygon>;
        for (const airspace of individuallyOutlineAsp) {
            if (airspace.airblocks.length === 1) {
                const polygon = this.buildPolygon(airspace.airblocks[0]);
                if (!unionPolygon) {
                    unionPolygon = polygon;
                } else {
                    unionPolygon = turf.union(unionPolygon, polygon);
                }
            }
        }
        if (Utils.isExists(unionPolygon) && unionPolygon.geometry.type === 'Polygon') {
            const unionLatLongCoordinates: Array<LatLonPoint> = unionPolygon.geometry.coordinates[0].map(position =>
                TurfUtils.toLatLonPoint(position),
            );
            const airblock: Airblock3d = {
                airblock: {
                    points: unionLatLongCoordinates,
                    id: 'union',
                },
            };
            return [airblock, ...aspNotContiguous.map(asp => this.getAllAirblocks(asp)).flat()];
        } else {
            // console.timeEnd(airspace.reference.id + " getAirspaceOutline");
            //if the union returns a MultiPolygon is because the polygons are not contiguous (should not happen in a airspace definition)
            return [
                ...individuallyOutlineAsp.map(asp => asp.airblocks).flat(),
                ...aspNotContiguous.map(asp => this.getAllAirblocks(asp)).flat(),
            ];
        }
    }

    public static getAllAirblocks(airspace: Airspace): Airblock3d[] {
        const output: Airblock3d[] = [];
        if (airspace.composedAirspace) {
            output.push(...airspace.composedAirspace.map(cAsp => this.getAllAirblocks(cAsp)).flat());
        }
        if (airspace.airblocks) {
            output.push(...airspace.airblocks);
        }
        return output;
    }

    static buildPolygons(airspace: Airspace): Feature<Polygon>[] {
        let polygons: Feature<Polygon>[] = [];
        for (const airspace1 of airspace.composedAirspace) {
            polygons = polygons.concat(this.buildPolygons(airspace1));
        }
        for (const ab3 of airspace.airblocks) {
            if (ab3.airblock && ab3.airblock.points) {
                polygons.push(this.buildPolygon(ab3));
            }
        }
        return polygons;
    }

    /**
     * Return true if the last point has the same coordinates as the first point.
     * When using AIXM data, this will be the case.
     * When using CACD data, this will not be the case.
     * @param points
     */
    static isClosed(points: Array<LatLonPoint>) {
        const firstPt = points[0];
        const lastPt = points[points.length - 1];
        return firstPt.latitude === lastPt.latitude && firstPt.longitude === lastPt.longitude;
    }

    static buildPolygon(ab3: Airblock3d): Feature<Polygon> {
        return MapUtils.buildPolygonFromAirblock(ab3?.airblock);
    }

    static buildPolygonFromAirblock(ab: Airblock): Feature<Polygon> {
        if (!ab || !ab.points) return null;
        const airblockCoordinates = ab.points.map(abPt => [abPt.longitude, abPt.latitude]);
        if (!MapUtils.isClosed(ab.points)) {
            airblockCoordinates.push(airblockCoordinates[0]);
        }
        const a = turf.polygon([[...airblockCoordinates]]);
        return a;
    }

    static intersects(tv: MapTrafficVolume, levelBand: FlightLevelBand) {
        switch (tv.trafficVolume.refLocation.refType) {
            case RefLocationType.AD:
            case RefLocationType.AZ:
                return levelBand.min.value <= 0;
            case RefLocationType.SP:
            case RefLocationType.DBP:
                return (
                    !tv.trafficVolume.flightLevelBand ||
                    FlightLevelUtils.intersects(tv.trafficVolume.flightLevelBand, levelBand)
                );
            case RefLocationType.AS:
                return MapUtils.airspaceIntersects(tv.location.airspace, levelBand);
        }
    }

    static airspaceIntersects(airspace: Airspace, levelBand: FlightLevelBand) {
        for (const airblock of airspace.airblocks) {
            if (FlightLevelUtils.intersects({ min: airblock.minLevel, max: airblock.maxLevel }, levelBand)) return true;
        }
        for (const composedAirspace of airspace.composedAirspace) {
            if (MapUtils.airspaceIntersects(composedAirspace, levelBand)) return true;
        }
        return false;
    }

    static getExtendedMonitorableTvAlert(
        tv: ExtendedMonitoredTv,
        countType: CountType,
        timePeriod: TimePeriod,
        showLoadInactivePeriods: boolean,
    ): AlertType {
        if (Utils.isNotExists(timePeriod?.from) || Utils.isNotExists(timePeriod?.to)) return null;
        const monitoredPeriod =
            CountType.ENTRY === countType
                ? MonitoredLoadUtils.getMonitoredPeriod(tv.loadValues)
                : MonitoredLoadUtils.getOccupancyMonitoredPeriod(tv.occupancyLoadValues);
        if (!monitoredPeriod) {
            //TODO this should be fixed when the computation of load values for non monitored tvs is computed
            console.warn('Not able to compute monitored period of extended monitored tv', tv);
            return null;
        }
        const inactivePeriods =
            monitoredPeriod && tv.activePeriods
                ? MonitoredLoadUtils.getInactivePeriods(monitoredPeriod, tv.activePeriods)
                : [];
        //for inactive time periods checks it just assumes it's not applicable (neutral) if the target time period @param timePeriod is fully covered by any of the inactive periods, if any part of it is active we consider it
        if (!showLoadInactivePeriods && Utils.contains(inactivePeriods, timePeriod, TimePeriodsContains))
            return AlertType.NEUTRAL;
        if (CountType.ENTRY === countType) {
            const tvsOfInterest = tv.loadValues.filter(
                loadValue =>
                    TimePeriodUtils.intersects(loadValue.timePeriod, timePeriod) &&
                    (showLoadInactivePeriods ||
                        !Utils.contains(inactivePeriods, loadValue.timePeriod, TimePeriodsContains)),
            );
            const redTvs = tvsOfInterest.filter(
                loadValue => LoadClassification.OVERLOAD === loadValue.loadClassification,
            );
            if (redTvs?.length > 0) {
                return AlertType.RED;
            }
            const orangeTvs = tvsOfInterest.filter(
                loadValue => LoadClassification.HIGH_THRESHOLD === loadValue.loadClassification,
            );
            if (orangeTvs?.length > 0) {
                return AlertType.ORANGE;
            }
            return AlertType.NEUTRAL;
        } else {
            const smallestDuration = Math.min(...tv.occupancyLoadValues.map(olv => olv.durationInMin));
            const applicableOccLoadValues = tv.occupancyLoadValues.filter(
                olv =>
                    olv.durationInMin === smallestDuration &&
                    olv.occupancyLoadValues.filter(
                        olv =>
                            TimePeriodUtils.intersects(olv.timePeriod, timePeriod) &&
                            !Utils.contains(inactivePeriods, olv.timePeriod, TimePeriodsContains),
                    ).length > 0,
            );

            const redTvs = applicableOccLoadValues?.filter(
                olv =>
                    olv.occupancyLoadValues.filter(
                        olv =>
                            LoadClassification.OVERLOAD === olv.loadClassification &&
                            TimePeriodUtils.intersects(olv.timePeriod, timePeriod),
                    ).length > 0,
            );

            if (redTvs?.length > 0) {
                return AlertType.RED;
            }
            const orangeTvs = applicableOccLoadValues?.filter(
                olv =>
                    olv.occupancyLoadValues.filter(
                        olv =>
                            LoadClassification.HIGH_THRESHOLD === olv.loadClassification &&
                            TimePeriodUtils.intersects(olv.timePeriod, timePeriod),
                    ).length > 0,
            );
            if (orangeTvs?.length > 0) {
                return AlertType.ORANGE;
            }
            return AlertType.NEUTRAL;
        }
    }

    static getConvectionMap = memoizeOne(
        (
            convectionData: NetworkMeteoForecast,
            airspaceDef: { [key: string]: CORE.Airspace },
            predictionTimePeriod?: Date | TimePeriod,
        ): Array<MapConvection> => {
            if (convectionData) {
                if (Utils.isNotEmpty(convectionData.polygonMeteoForecasts)) {
                    return convectionData.polygonMeteoForecasts
                        .filter(
                            conv =>
                                !predictionTimePeriod ||
                                TimePeriodUtils.intersects(conv.validityPeriod, predictionTimePeriod),
                        )
                        .map(conv => {
                            const tvAspDef = {};
                            Object.entries(conv.impactedTvs).forEach(
                                ([tv, airspaceRef]: [string, AirspaceRef]) =>
                                    (tvAspDef[tv] = airspaceDef[airspaceRef.id]),
                            );
                            return { convection: conv, tvAirspaceMap: tvAspDef };
                        });
                } else if (convectionData.forecastPerTvName) {
                    return Object.values(convectionData.forecastPerTvName)
                        .filter(
                            conv =>
                                !predictionTimePeriod ||
                                TimePeriodUtils.intersects(conv.validityPeriod, predictionTimePeriod),
                        )
                        .map(conv => {
                            const tvAspDef = {};
                            tvAspDef[conv.id] = airspaceDef[conv.airspaceRef.id];
                            return { convection: conv, tvAirspaceMap: tvAspDef };
                        });
                }
            }
            return undefined;
        },
    );

    static shouldDisplayPolygonConvection = (convectionPeriod: TimePeriod, timePeriod: TimePeriod): boolean => {
        if (TimeUtils.isEquals(timePeriod.from, timePeriod.to)) return true;
        if (AppContext.getConvectionSource() !== ConvectionSource.DWD_ICONV) return true;

        // We're in TimePeriod Mode and displaying DWD_ICON ==> Display only first and last polygon
        if (TimePeriodUtils.intersects(convectionPeriod, timePeriod.from)) return true;

        return TimePeriodUtils.intersects(convectionPeriod, timePeriod.to);
    };

    static getPolygonIconvConvectionStyle = (
        level: number,
        convectionValidity: TimePeriod,
        timePeriod: TimePeriod,
    ): style.Style => {
        let color = OLMapStyles.WHITE;
        let fillColor: number[] | ColorLike;
        switch (level) {
            case 0:
                color = OLMapStyles.WHITE;
                fillColor = OLMapStyles.WHITE_80_TRANSPARENT;
                break;
            case 1:
                color = OLMapStyles.YELLOW;
                fillColor = OLMapStyles.YELLOW_80_TRANSPARENT;
                break;
            case 2:
                color = OLMapStyles.ORANGE;
                fillColor = OLMapStyles.ORANGE_80_TRANSPARENT;
                break;
            case 3:
                color = OLMapStyles.RED;
                fillColor = OLMapStyles.RED_80_TRANSPARENT;
                break;
            case 4:
                color = OLMapStyles.PURPLE;
                break;
        }

        const timePeriodMode = !TimeUtils.isEquals(timePeriod.from, timePeriod.to);
        const hasFillColor =
            AppContext.getConvectionSource() === ConvectionSource.DWD_ICONV &&
            timePeriodMode &&
            TimePeriodUtils.intersects(convectionValidity, timePeriod.from);
        return new style.Style({
            fill: new style.Fill({
                color: hasFillColor ? fillColor : OLMapStyles.BLACK_90_TRANSPARENT,
            }),
            stroke: new style.Stroke({
                color: color,
                width: 2,
            }),
        });
    };

    static getPolygonDWDConvectionStyle = (fraction: ConvectionFraction, likeliness: Likeliness): style.Style => {
        let color = OLMapStyles.WHITE;
        switch (likeliness) {
            case Likeliness.LESS_LIKELY:
                switch (fraction) {
                    case ConvectionFraction.ISOL:
                        break;
                    case ConvectionFraction.CLST:
                        color = [255, 255, 0, 1];
                        break;
                    case ConvectionFraction.WSPR:
                        color = [252, 140, 4, 1];
                        break;
                }
                break;
            case Likeliness.LIKELY:
                switch (fraction) {
                    case ConvectionFraction.ISOL:
                        color = [255, 255, 127, 1];
                        break;
                    case ConvectionFraction.CLST:
                        color = [255, 192, 0, 1];
                        break;
                    case ConvectionFraction.WSPR:
                        color = [255, 0, 0, 1];
                        break;
                }
                break;
            case Likeliness.VERY_LIKELY:
                switch (fraction) {
                    case ConvectionFraction.ISOL:
                        color = [255, 217, 102, 1];
                        break;
                    case ConvectionFraction.CLST:
                        color = [255, 102, 102, 1];
                        break;
                    case ConvectionFraction.WSPR:
                        color = [112, 48, 160, 1];
                        break;
                }
                break;
        }
        return new style.Style({
            fill: new style.Fill({
                color: OLMapStyles.BLACK_90_TRANSPARENT,
            }),
            stroke: new style.Stroke({
                color: color,
                width: 2,
            }),
        });
    };

    static calculateAreaKm2(coordinates: Coordinate[]): number {
        // Append the first coordinate to close the polygon
        const coords = [...coordinates, coordinates[0]];

        // Radius of Earth in kilometers
        const R = 6371;

        // Initialize the area variable
        let total = 0;

        // Loop through the polygon's edges
        for (let i = 0; i < coords.length - 1; i++) {
            const [lon1, lat1] = coords[i];
            const [lon2, lat2] = coords[i + 1];

            // Convert degrees to radians
            const lon1Rad = (lon1 * Math.PI) / 180;
            const lat1Rad = (lat1 * Math.PI) / 180;
            const lon2Rad = (lon2 * Math.PI) / 180;
            const lat2Rad = (lat2 * Math.PI) / 180;

            // Calculate the area using the spherical excess formula (Shoelace formula for geodesic polygons)
            total += lon1Rad * lat2Rad - lon2Rad * lat1Rad;
        }

        // Return the absolute area in square kilometers
        return (Math.abs(total) * R * R) / 2; // Divide by 2 as per the Shoelace formula
    }

    static isPolygonValid(coordinates: Coordinate[]): boolean {
        const coords = [...coordinates, coordinates[0]];

        const segments: [Coordinate, Coordinate][] = [];
        for (let i = 0; i < coords.length - 1; i++) {
            segments.push([coords[i], coords[i + 1]]);
        }

        for (let i = 0; i < segments.length; i++) {
            for (let j = i + 1; j < segments.length; j++) {
                if (i === j || Math.abs(i - j) === 1 || (i === 0 && j === segments.length - 1)) continue;
                if (this.segmentsIntersect(...segments[i], ...segments[j])) return false;
            }
        }

        return true;
    }

    static segmentsIntersect(p1: Coordinate, p2: Coordinate, q1: Coordinate, q2: Coordinate): boolean {
        const ccw = (a: Coordinate, b: Coordinate, c: Coordinate) =>
            (c[1] - a[1]) * (b[0] - a[0]) > (b[1] - a[1]) * (c[0] - a[0]);
        return ccw(p1, q1, q2) !== ccw(p2, q1, q2) && ccw(p1, p2, q1) !== ccw(p1, p2, q2);
    }
}

const TimePeriodsContains: Comparator = (t1: TimePeriod, t2: TimePeriod): boolean => {
    return TimePeriodUtils.contains(t1, t2);
};
